import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Alert, Snackbar } from '@mui/material';
import type { AlertColor } from '@mui/material';

interface ToastMessage {
  key: number;
  message: string;
  severity: AlertColor;
}

interface ToastContextValue {
  showError: (message: string) => void;
  showSuccess: (message: string) => void;
  showInfo: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const AUTO_HIDE_DURATION = 6000;

/**
 * Global toast/snackbar provider. Renders a single MUI Snackbar+Alert that
 * surfaces transient messages (errors, success, info) as a popup at the bottom
 * of the viewport, regardless of where in the page the triggering action lives.
 * The bottom placement keeps action feedback clear of the fixed navigation bar.
 *
 * Newer messages replace the currently shown one cleanly (the open snackbar is
 * closed first so the slide transition restarts with the new content).
 */
interface ToastProviderProps {
  children: React.ReactNode;
  placement?: 'top' | 'bottom';
  /** Optional caller layer; normal app toasts retain the theme's default. */
  zIndex?: number;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children, placement = 'bottom', zIndex }) => {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState<ToastMessage | null>(null);
  const keyRef = useRef(0);

  const enqueue = useCallback((message: string, severity: AlertColor) => {
    if (!message) return;
    keyRef.current += 1;
    setCurrent({ key: keyRef.current, message, severity });
    // Reopen even if already open so the duplicate/replacement is visible.
    setOpen(false);
    // Defer to next tick so the Snackbar re-mounts the message cleanly.
    requestAnimationFrame(() => setOpen(true));
  }, []);

  const value = useMemo<ToastContextValue>(
    () => ({
      showError: (message: string) => enqueue(message, 'error'),
      showSuccess: (message: string) => enqueue(message, 'success'),
      showInfo: (message: string) => enqueue(message, 'info'),
    }),
    [enqueue]
  );

  const handleClose = useCallback((_event?: unknown, reason?: string) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  }, []);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Snackbar
        key={current?.key}
        open={open}
        autoHideDuration={AUTO_HIDE_DURATION}
        onClose={handleClose}
        anchorOrigin={{ vertical: placement, horizontal: 'center' }}
        sx={{
          ...(placement === 'bottom' ? {
              bottom: {
                xs: 'calc(8px + env(safe-area-inset-bottom))',
                sm: 'calc(24px + env(safe-area-inset-bottom))',
              },
              px: { xs: 1, sm: 0 },
            } : {}),
          ...(zIndex !== undefined ? { zIndex } : {}),
        }}
      >
        {current ? (
          <Alert
            severity={current.severity}
            variant="filled"
            onClose={() => setOpen(false)}
            sx={{ width: '100%', maxWidth: 600 }}
          >
            {current.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </ToastContext.Provider>
  );
};

/**
 * Access the global toast API. Must be used within a <ToastProvider>.
 */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
}
